/**
 * Formats a US phone number as (XXX) XXX-XXXX while the user types.
 *
 * A US country code is unwrapped ("+14155940795" / "14155940795" → the
 * 10-digit mask) instead of shifting the mask and truncating the last digit.
 * A "+" number with MORE than 10 digits is kept as +digits (no US mask) so a
 * typical international paste survives. A "+" number totalling exactly 10
 * digits is irreducibly ambiguous with a US number carrying a stray "+"
 * (e.g. "+4155940795" vs Swiss +41...) — the US reading wins by design for
 * this US-market product, so such a paste IS US-masked and later normalizes
 * to +1.
 */
export function formatPhone(raw: string): string {
  const trimmed = raw.trim()
  const allDigits = trimmed.replace(/\D/g, '')
  const digits = allDigits.length >= 11 && allDigits.startsWith('1')
    ? allDigits.slice(1)
    : allDigits
  if (trimmed.startsWith('+') && digits.length > 10) return `+${allDigits.slice(0, 15)}`
  const d = digits.slice(0, 10)
  if (d.length === 0) return ''
  if (d.length <= 3) return `(${d}`
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** Formats a TIN as XX-XXXXXXX while the user types. */
export function formatTin(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 9)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}-${digits.slice(2)}`
}

/**
 * Strip a trailing `(12345)` payer-ID suffix from a display name.
 * RevolutionEHR stores payers as e.g. "EyeMed (31165)"; the parenthesized
 * ID confuses PayerSearch's exact-match resolver, so we strip it when
 * prefilling the eligibility form.
 */
export function stripPayerIdSuffix(name: string): string {
  return name.replace(/\s*\(\d+\)\s*$/, '').trim()
}

/** Convert minutes-since-midnight (0–1439) to "H:MM AM/PM". Returns "" for null/invalid. */
export function formatMinutesAsTime(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0 || minutes >= 1440) return ''
  const h24 = Math.floor(minutes / 60)
  const mm = minutes % 60
  const isPM = h24 >= 12
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${String(mm).padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`
}

/** Format a start/end minute pair as "2:30 PM – 3:00 PM". Falls back to start-only or "" as inputs degrade. */
export function formatAppointmentTimeRange(
  startMin: number | null | undefined,
  endMin: number | null | undefined,
): string {
  const start = formatMinutesAsTime(startMin)
  if (!start) return ''
  const end = formatMinutesAsTime(endMin)
  return end ? `${start} – ${end}` : start
}

/**
 * Format an ISO `YYYY-MM-DD` date as US `MM/DD/YYYY` for display, by string
 * surgery rather than `new Date()` — so there's no timezone shift. Returns the
 * input unchanged when it isn't a 3-part ISO date, which also makes '' → ''
 * convenient for "no value" fallbacks (`formatIsoDateUs(x) || placeholder`).
 *
 * Several components carry their own local copy of this (DOB/appointment-date
 * cells); this is the shared one to converge on.
 */
export function formatIsoDateUs(iso: string): string {
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  return `${parts[1]}/${parts[2]}/${parts[0]}`
}

const MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Format an ISO `YYYY-MM-DD` date as a short "Jun 30" label (the Recovery Loop's
 * `appt Jun 30` notation), by string surgery rather than `new Date()` — so
 * there's no timezone shift. Returns the input unchanged when it isn't an ISO
 * date with a valid month, which also makes '' → '' convenient for fallbacks.
 */
export function formatIsoDateShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return iso
  const month = MONTH_ABBREV[Number(m[2]) - 1]
  return month ? `${month} ${Number(m[3])}` : iso
}

/**
 * Format an instant as its browser-local calendar day, e.g. "Jul 3, 2026" —
 * the "older than a week" fallback of relative timestamps (Recovery Loop rows,
 * the check-failure slideout). Locale pinned to en-US: the bare
 * toLocaleDateString() follows the browser locale and renders DD/MM/YYYY
 * outside the US, misreading as the wrong date. Returns '' when invalid.
 */
export function formatDateShortUs(date: Date): string {
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Format a payer-verification instant as a dispute-ready date stamp, e.g.
 * "Jun 3, 2026, 2:14 PM EDT". Rendered in the given clinic timezone so the date
 * matches the clinic's local calendar day (what a payer dispute hinges on);
 * `timeZoneName: 'short'` keeps the zone explicit on the filed record. Falls
 * back to the browser timezone when `timeZone` is absent, and returns the raw
 * input unchanged if it can't be parsed (so a bad value never throws in the UI).
 */
export function formatCheckTimestamp(iso: string, timeZone?: string): string {
  const date = new Date(iso)
  // An unparseable date would otherwise render the literal "Invalid Date" (the
  // catch below only fires on a bad timeZone, since toLocaleString returns that
  // string rather than throwing) — show the raw input instead.
  if (Number.isNaN(date.getTime())) return iso
  try {
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: timeZone || undefined,
      timeZoneName: 'short',
    })
  } catch {
    return iso
  }
}
